/**
 * Bitcoin Service
 * Handles Bitcoin-related operations
 */
import apiClient from './apiClient';
import { ensureAuthenticated } from './authService';

export interface BitcoinPrice {
  currency: string;
  rate: number;
  timestamp: string;
}

export interface BitcoinPayment {
  id: string;
  amount: number;
  amountSatoshi: number;
  address: string;
  status: 'pending' | 'confirmed' | 'expired';
  createdAt: string;
  expiresAt: string;
  confirmedAt?: string;
}

/**
 * BitcoinPriceData interface for compatibility with components using the old API
 */
export interface BitcoinPriceData {
  currentPrice: number;
  priceChange24h: number;
  priceChangePercentage24h: number;
  lastUpdated: Date;
}

/**
 * Get the current Bitcoin price
 */
export const getBitcoinPrice = async (): Promise<BitcoinPrice> => {
  try {
    const response = await apiClient.get('/bitcoin/price');
    return response.data;
  } catch (error) {
    console.error('Error fetching Bitcoin price:', error);
    throw new Error('Failed to fetch Bitcoin price');
  }
};

/**
 * Calculate USD amount for given BTC
 */
export const btcToUsd = async (btcAmount: number): Promise<number> => {
  const btcPrice = await getBitcoinPrice();
  return btcAmount * btcPrice.rate;
};

/**
 * Convert USD to Bitcoin amount
 */
export const convertUsdToBtc = async (usdAmount: number): Promise<number> => {
  try {
    const response = await apiClient.get(`/bitcoin/convert?usd=${usdAmount}`);
    return response.data.btcAmount;
  } catch (error) {
    console.error('Error converting USD to BTC:', error);
    throw new Error('Failed to convert USD to BTC');
  }
};

/**
 * Calculate credits equivalent in BTC
 */
export const creditsToBtc = async (credits: number): Promise<number> => {
  // In our system, 1 credit = 1 USD for simplicity
  return await convertUsdToBtc(credits);
};

/**
 * Format BTC amount for display
 */
export const formatBtcAmount = (btcAmount: number): string => {
  // For very small amounts (less than 0.001 BTC)
  if (btcAmount < 0.001) {
    return btcAmount.toFixed(8);
  }

  // For small amounts (less than 0.01 BTC)
  if (btcAmount < 0.01) {
    return btcAmount.toFixed(6);
  }

  // For medium amounts (less than 1 BTC)
  if (btcAmount < 1) {
    return btcAmount.toFixed(5);
  }

  // For larger amounts (1 BTC or more)
  return btcAmount.toFixed(4);
};

/**
 * Format BTC price change with appropriate sign
 */
export const formatPriceChange = (change: number, percentage = false): string => {
  const prefix = change >= 0 ? '+' : '';

  if (percentage) {
    return `${prefix}${change.toFixed(2)}%`;
  }

  return `${prefix}$${Math.abs(change).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;
};

/**
 * Get BTC balance equivalent for a user's credit balance
 */
export const getBtcEquivalent = async (creditBalance: number): Promise<string> => {
  const btcAmount = await creditsToBtc(creditBalance);
  return formatBtcAmount(btcAmount);
};

/**
 * Create a new Bitcoin payment for credit top-up
 */
export const createBitcoinPayment = async (usdAmount: number): Promise<BitcoinPayment> => {
  try {
    // Check if user is authenticated
    const user = await ensureAuthenticated();
    if (!user) {
      throw new Error('You must be logged in to create a Bitcoin payment');
    }

    const response = await apiClient.post('/bitcoin/payments', {
      usdAmount
    });
    return response.data;
  } catch (error) {
    console.error('Error creating Bitcoin payment:', error);
    throw new Error('Failed to create Bitcoin payment');
  }
};

/**
 * Get a Bitcoin payment by ID
 */
export const getBitcoinPayment = async (paymentId: string): Promise<BitcoinPayment> => {
  try {
    const response = await apiClient.get(`/bitcoin/payments/${paymentId}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching Bitcoin payment:', error);
    throw new Error('Failed to fetch Bitcoin payment');
  }
};

/**
 * Get all Bitcoin payments for the current user
 */
export const getUserBitcoinPayments = async (): Promise<BitcoinPayment[]> => {
  try {
    const response = await apiClient.get('/bitcoin/payments');
    return response.data;
  } catch (error) {
    console.error('Error fetching user Bitcoin payments:', error);
    return [];
  }
};

/**
 * Check if a Bitcoin payment is confirmed
 */
export const checkBitcoinPaymentStatus = async (paymentId: string): Promise<BitcoinPayment> => {
  try {
    const response = await apiClient.get(`/bitcoin/payments/${paymentId}/status`);
    return response.data;
  } catch (error) {
    console.error('Error checking Bitcoin payment status:', error);
    throw new Error('Failed to check Bitcoin payment status');
  }
};

/**
 * Fetches current Bitcoin price data (compatibility method)
 * This version uses the new API client instead of direct fetch
 */
export const fetchBitcoinPrice = async (): Promise<BitcoinPriceData> => {
  try {
    const response = await apiClient.get('/bitcoin/price-data');
    return response.data;
  } catch (error) {
    console.error('Error fetching Bitcoin price data:', error);

    // Create fallback data if fetch fails
    return {
      currentPrice: 60000, // Example fallback price
      priceChange24h: 0,
      priceChangePercentage24h: 0,
      lastUpdated: new Date()
    };
  }
};
